/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '../capabilities/storage';
import {isBefore} from '../utils/DateUtil';
import {safeJSONStringify} from '../utils/JSONUtils';
import {parseJWT} from '../utils/TokenUtils';

/**
 * Provides a reference to the CTC JWT Authorization storage key used for
 * persisting and retrieving the authorization token and claims from CTC
 * login (i.e. non OIDC/Okta Authentication).
 */
export const authorizationKey = 'ctc-auth-token';

/**
 * Provides the single point of access for setting the CTC Authorization Token
 * returned by the CTC login Service.
 *
 * **Note: This function is to be considered an internal function that is only
 * to be invoked via the `login` function of the Connect Services Module. It is
 * simply provided in order to access the Auth Token returned by CTC when OIDC
 * authentication is not being used.**
 */
export const setSessionToken = (token: string) => {
  setLocalStorage(authorizationKey, safeJSONStringify(
    parseJWT(token)
  ));
};

/**
 * Returns the CTC Authorization Token if authentication has been facilitated via
 * the CTC login Service and the token has not expired.
 *
 * **Note: This function is to be considered an internal function that is only
 * to be reference via the `AuthenticationManager` API in order to ensure that
 * all references to the authToken are facilitated via a single, unified API.
 * This function simply provides a fallback for when the AuthenticationManager
 * does not have a token (which will be the case when the Auth Token is returned
 * by CTC rather than OIDC).**
 */
export const getSessionToken = (): string | undefined => {
  const {expiration, token} = getLocalStorage(authorizationKey) || {};
  if (expiration) {
    if (isBefore(new Date(expiration))) {
      return token;
    } else {
      invalidateSessionToken();
    }
  }
};

/**
 * Returns the CTC Authorization Token Expiration if authentication has been
 * facilitated via the CTC login Service and the token has not expired.
 *
 * **Note: This function is to be considered an internal function that is only
 * to be reference via the `AuthenticationManager` API in order to ensure that
 * all references to the authToken are facilitated via a single, unified API.
 * This function simply provides a fallback for when the AuthenticationManager
 * does not have a token (which will be the case when the Auth Token is returned
 * by CTC rather than OIDC).**
 */
export const getSessionTokenExpiration = (): string | undefined => {
  const {expiration} = getLocalStorage(authorizationKey) || {};
  return expiration;
};

/**
 * Provides a utility which effectively invalidates the current CTC session token
 * by removing it from the store, thus subsequent calls to `getSessionToken` will
 * return `undefined.
 */
export const invalidateSessionToken = () => {
  deleteLocalStorage(authorizationKey);
};
